import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import Select from 'components/Select/Select';
import {
  ADDRESS_COUNTRY,
  INDUSTRY_CODE,
  SECTOR_CODE,
} from 'constants/formFields';
import { BUTTON_VARIANT } from 'constants/interfaces';
import useFieldsErrors from 'hooks/useFieldsErrors';
import useGeoLocations from 'hooks/useGeoLocations';
import i18n from 'i18n';
import { useCreateSectorCustomSpecificTopicMutation } from 'store/slices/standards/apis/standardsApi';

import useCreateIndustryForm from './hooks/useCreateIndustryForm';

import type { INaceExtractedValue } from 'store/slices/dataSets/interfaces/INaceExtractedValue';
import type ICreateSpecificRequest from 'store/slices/standards/interfaces/ICreateSpecificRequest';
import type IFieldError from 'store/types/IFieldError';
import type { TSpecificCreateSchema } from 'utils/validators';
import type ICreateIndustry from './interfaces/ICreateIndustry';

import styles from './scss/CreateIndustry.module.scss';

function CreateIndustry({
  open, onClose, title,
  recordId, setCustomOffset, setOpenModal,
  naceCodesData = [],
}: ICreateIndustry) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const { setFieldsErrors } = useFieldsErrors();
  const [createSectorCustomSpecificTopic] = useCreateSectorCustomSpecificTopicMutation();

  const isLoading = false;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    fieldsName,
  } = useCreateIndustryForm({ isPageLoading: isLoading });

  const onCancel = () => {
    onClose();
    reset();
  };

  const onAddIndustry = (item: ICreateSpecificRequest) => {
    createSectorCustomSpecificTopic({ body: item, maId: recordId }).then(() => {
      toast.success(i18n.t('notifications.createdSectorRisk'));
      setOpenModal(false);
      setCustomOffset(0);
    }).catch((errors) => {
      setFieldsErrors<TSpecificCreateSchema>({ errors, setError, fieldsName });
    });
  };

  const {
    onFindCountries, onChangeCountry, defaultCountries,
  } = useGeoLocations<TSpecificCreateSchema>({ watch, setValue });

  const onCreateInvestorSubmit = handleSubmit((data) => {
    onAddIndustry({
      country: data[ADDRESS_COUNTRY].value || '',
      naceSection: data[INDUSTRY_CODE].value || '',
      naceCodes: data[SECTOR_CODE].map((item) => item.value) || [],

    });
    onCancel();
  });

  const naceDivisions = (watch(INDUSTRY_CODE) as INaceExtractedValue)?.divisions || [];

  const onNaceSelectionChange = () => {
    setValue(SECTOR_CODE, []);
  };

  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div className={styles.root} ref={modalRef}>
        <div className={styles.formWrapper}>
          <Controller
            control={control}
            name={ADDRESS_COUNTRY}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.country')}
                placeholder={t('materialityAssessment.placeholder.selectCountry')}
                value={value?.value ? value : null}
                onChange={(newValue) => {
                  onChange(newValue);
                  onChangeCountry();
                }}
                error={error?.message}
                loadOptions={onFindCountries}
                defaultOptions={defaultCountries}
                isSearchable
                isAsync
                isClearable
                className={styles.asyncSelect}
                menuPosition="absolute"
                required
              />
            )}
          />
          <Controller
            control={control}
            name={INDUSTRY_CODE}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.industryCode')}
                placeholder={t('materialityAssessment.placeholder.selectIndustry')}
                options={naceCodesData}
                value={value?.value ? value : null}
                onChange={
                  (newValue) => {
                    onChange(newValue);
                    onNaceSelectionChange();
                  }
                }
                error={(error as IFieldError)?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />
          <Controller
            control={control}
            name={SECTOR_CODE}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.sector')}
                placeholder={t('materialityAssessment.placeholder.selectSector')}
                options={naceDivisions}
                value={value || null}
                onChange={onChange}
                error={(error as IFieldError)?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
                isMulti
              />
            )}
          />
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={onCancel}
          />
          <Button
            title={t('common.add')}
            onClick={onCreateInvestorSubmit}
            className={styles.button}
          />
        </div>
      </div>
    </Modal>
  );
}

export default CreateIndustry;
